import { useCallback } from "react";
import { Segment, Header, Icon } from "semantic-ui-react";
import { useDropzone, FileRejection } from "react-dropzone";
import { UserFile } from "./../../models/UserFile";
import { v4 as uuid } from "uuid";

interface Props {
  multiple?: boolean;
  maxSizeByte?: number;
  accept?: string[];
  onAcceptedFilesSelected: (userFiles: UserFile[]) => void;
  onRejectedFilesSelected: (userFiles: UserFile[]) => void;
}

const FileDropZone = ({ multiple, maxSizeByte, accept, onAcceptedFilesSelected, onRejectedFilesSelected }: Props) => {
  const handleFileDrop = (acceptedfiles: File[], fileRejections: FileRejection[]) => {
    const userFiles: UserFile[] = [];

    acceptedfiles.forEach((file) => {
      userFiles.push({ id: uuid(), file } as UserFile);
    });

    fileRejections.forEach((rejection) => {
      userFiles.push({
        id: uuid(),
        file: rejection.file,
        uploadStatus: { fileErrors: rejection.errors.map((err) => err.message) },
      } as UserFile);
    });

    if (userFiles.length) {
      handleFilesSelected(userFiles);
    }
  };

  const handleFilesSelected = (userFiles: UserFile[]) => {
    var acceptedFiles = userFiles.filter((f) => !f.uploadStatus?.fileErrors?.length);
    var rejectedFiles = userFiles.filter((f) => f.uploadStatus?.fileErrors?.length);

    if (acceptedFiles.length) {
      onAcceptedFilesSelected(acceptedFiles);
    } else if (rejectedFiles.length) {
      onRejectedFilesSelected(rejectedFiles);
    }
  };

  const onDrop = useCallback(handleFileDrop, [handleFilesSelected]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple,
    maxSize: maxSizeByte,
    accept: accept?.join(","),
  });

  return (
    <div {...getRootProps()}>
      <Segment placeholder textAlign="center">
        <Header as="h4" icon>
          <Icon name="cloud upload" />
        </Header>
        <Segment.Inline>
          <input {...getInputProps()} />
          <i>Drag 'n' drop {multiple ? "some files" : "a file"} here, or click to browse files</i>
          {maxSizeByte && (
            <Header.Subheader>
              <i>Maximum file size allowed: {maxSizeByte / 1024 / 1024} MB</i>
            </Header.Subheader>
          )}
          {accept && (
            <Header.Subheader>
              <i>Only {accept.join(", ")} file will be accepted</i>
            </Header.Subheader>
          )}
        </Segment.Inline>
      </Segment>
    </div>
  );
};

export default FileDropZone;
